/* global __app_id, __firebase_config, __initial_auth_token */
import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged,
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut
} from 'firebase/auth';
import { getFirestore, doc, getDoc, addDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection, query, where, getDocs, collectionGroup, connectFirestoreEmulator } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { Html5QrcodeScanner } from 'html5-qrcode';
import {
  User, Plus, Search, Hotel, Mail, CheckCircle, XCircle, BarChart2,
  Calendar, CreditCard, Tag, Download, Lock, Printer, Eye, Trash2, FileText, Smartphone, Briefcase, LogIn, LogOut, Settings, Camera
} from 'lucide-react';

// Define the available outlets for the dropdown
const HOTELS = [
  "Corinthia Caterers",
  "Corinthia St George's Bay",
  "Corinthia Palace",
  "MFCC",
  "Costa Coffee",
  "Radisson Blu Golden Sands",
  "Radisson Blu St Julians",
  "QP",
  "Danish Bakery",
  "Verdi Gzira Promenade",
  "Verdi St George's Bay Marina",
  "Swan",
  "Corinthia HO",
  "Vivaldi Hotel",
  "RQT",
  "Palace Landscaping",
  "Palazzo Leone"
];

// QR Code Canvas Component for rendering QR images
const QRCodeCanvas = ({ value, size = 128, id }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current && typeof window.QRious !== 'undefined') {
      try {
        new window.QRious({
          element: canvasRef.current,
          value: value,
          size: size,
          level: 'H'
        });
      } catch (error) {
        console.error("Error generating QR code with QRious:", error);
      }
    }
  }, [value, size, id]);

  return <canvas ref={canvasRef} id={id} className="rounded-lg shadow-md" />;
};

// Main App Component
function App() {
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseInitError, setFirebaseInitError] = useState(false);
  const [hotelSales, setHotelSales] = useState({});
  const [overallHotelSales, setOverallHotelSales] = useState({});
  const [tickets, setTickets] = useState([]);
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanMessage, setScanMessage] = useState(''); // Corrected: useState('')
  const [activeTab, setActiveTab] = useState('sell');
  const [allTickets, setAllTickets] = useState([]); // This will now hold all tickets for all users

  const [addTicketStatus, setAddTicketStatus] = useState(null);
  const [addTicketMessage, setAddTicketMessage] = useState('');
  const [qrToDownload, setQrToDownload] = useState(null); // Corrected: useState(null)
  const [showQrDisplayModal, setShowQrDisplayModal] = useState(false);

  const [selectedViewHotel, setSelectedViewHotel] = useState('All Outlets');
  const [selectedAdminViewHotel, setSelectedAdminViewHotel] = useState('All Outlets');

  const [qriousLoaded, setQriousLoaded] = useState(false);
  const [html5QrScannerLoaded, setHtml5QrScannerLoaded] = useState(false);
  const [manualQrInput, setManualQrInput] = useState('');

  // Hardcoded password for deleting tickets (still retained for this specific action)
  const RESTRICTED_PASSWORD = "corinthia2025";

  // States for "View Last Entry" functionality
  const [lastAddedTicketId, setLastAddedTicketId] = useState(null);
  const [showLastEntryModal, setShowLastEntryModal] = useState(false);
  const [lastEntryTicketDetails, setLastEntryTicketDetails] = useState(null);
  const [editingBuyerName, setEditingBuyerName] = useState('');
  const [editingBuyerEmail, setEditingBuyerEmail] = useState('');
  const [editingHotelName, setEditingHotelName] = useState('');
  const [lastEntryEditStatus, setLastEntryEditStatus] = useState(null);
  const [lastEntryEditMessage, setLastEntryEditMessage] = useState('');

  // New states for delete functionality
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null); // Corrected: useState(null)
  const [deletePasswordInput, setDeletePasswordInput] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // New states for Mobile Number and Company ID
  const [editingBuyerMobile, setEditingBuyerMobile] = useState('');
  const [editingBuyerCompanyId, setEditingBuyerCompanyId] = useState('');

  // New states for Login/Signup Modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState(''); // Corrected: useState('')
  const [isLoginMode, setIsLoginMode] = useState(true);

  // States for Camera Scanning
  const [isScanning, setIsScanning] = useState(false);
  const [scanCameraError, setScanCameraError] = useState('');
  const [isProcessingQr, setIsProcessingQr] = useState(false);
  const html5QrScannerRef = useRef(null);


  // Refs for form inputs
  const buyerNameRef = useRef();
  const buyerEmailRef = useRef();
  const buyerMobileRef = useRef();
  const buyerCompanyIdRef = useRef();
  const hotelSelectRef = useRef();

  // Dynamically load QRious library via CDN
  useEffect(() => {
    if (document.getElementById('qrious-script')) {
      setQriousLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = "https://cdn.jsdelivr.net/npm/qrious@4.0.2/dist/qrious.min.js";
    script.id = "qrious-script";
    script.async = true;
    script.onload = () => { setQriousLoaded(true); };
    script.onerror = (e) => { console.error("Failed to load QRious script:", e); };
    document.body.appendChild(script);
  }, []);

  // Set html5-qrcode as loaded since it's imported as a module
  useEffect(() => {
    setHtml5QrScannerLoaded(true);
  }, []);

  // Firebase Initialization and Authentication
  useEffect(() => {
    console.log('Firebase Init: Outer useEffect started.');
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    let firebaseConfig = null;

    // Attempt to use Canvas-provided config for simulation environment
    if (typeof __firebase_config !== 'undefined' && __firebase_config !== null && String(__firebase_config).trim() !== '') {
      try {
        firebaseConfig = JSON.parse(String(__firebase_config));
        console.log('Firebase Init: Successfully parsed Canvas-provided firebaseConfig:', firebaseConfig);
      } catch (e) {
        console.error("Firebase Init: Error parsing Canvas-provided __firebase_config. It might be malformed JSON.", e);
      }
    } else {
      console.warn("Firebase Init: __firebase_config is undefined, null, or empty. Assuming deployment environment.");
    }

    // If Canvas config is not available or invalid, use the hardcoded values for deployment.
    // Users MUST replace these placeholders with their actual Firebase project config.
    if (!firebaseConfig || Object.keys(firebaseConfig).length === 0 || (firebaseConfig.apiKey && firebaseConfig.apiKey.includes("YOUR_"))) {
      console.warn("Firebase Init: Canvas-provided config missing or invalid. Falling back to hardcoded config for deployment.");
      firebaseConfig = {
        apiKey: "AIzaSyBkXUAHWgJxQtj25iNJPqlVRz5nfmBhJpY",
        authDomain: "corinthia-summer-party-app.firebaseapp.com",
        projectId: "corinthia-summer-party-app",
        storageBucket: "corinthia-summer-party-app.firebasestorage.app",
        messagingSenderId: "441864665837",
        appId: "1:441864665837:web:994576b606f22679c8aae4",
        measurementId: "G-9S0S2HYG6M"
      };
      if (firebaseConfig.apiKey.includes("YOUR_") || firebaseConfig.projectId.includes("YOUR_")) {
        console.error("Firebase Init: WARNING: HARDCODED CONFIGURATIONS STILL CONTAIN 'YOUR_' PLACEHOLDERS. YOUR DEPLOYED APP WILL NOT WORK CORRECTLY.");
        setFirebaseInitError(true);
        setLoading(false);
        return;
      }
    }

    let appInstance;
    let firestoreInstance;
    let authInstance;

    try {
      appInstance = initializeApp(firebaseConfig);
      firestoreInstance = getFirestore(appInstance);
      
      // Connect to Firestore emulator if running locally
      // TEMPORARILY DISABLED: Connect to production Firebase instead of emulator for cross-device testing
      if (false && (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost')) {
        try {
          connectFirestoreEmulator(firestoreInstance, 'localhost', 8080);
          console.log('Firebase Init: Connected to Firestore emulator on localhost:8080');
        } catch (error) {
          // Emulator connection might already be established
          console.log('Firebase Init: Firestore emulator connection already established or failed:', error.message);
        }
      } else {
        console.log('Firebase Init: Connecting to production Firebase (emulator disabled for cross-device testing)');
      }
      
      authInstance = getAuth(appInstance);

      setDb(firestoreInstance);
      setAuth(authInstance);
      console.log('Firebase Init: Firebase app, firestore, auth instances created and states set.');

      // Attempt to sign in automatically using __initial_auth_token or with a consistent approach
      const signInUser = async () => {
        try {
          if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
            console.log('Firebase Init: Attempting signInWithCustomToken...');
            await signInWithCustomToken(authInstance, __initial_auth_token);
            console.log('Firebase Init: signInWithCustomToken successful.');
          } else {
            // Check if there's a stored user preference for consistent anonymous auth
            const storedUserId = localStorage.getItem('corinthia-anonymous-user-id');
            if (storedUserId) {
              console.log('Firebase Init: Found stored anonymous user ID, but Firebase doesn\'t support resuming anonymous sessions.');
              console.log('Firebase Init: For cross-device access, please use email authentication.');
            }
            
            console.log('Firebase Init: Attempting signInAnonymously...');
            const result = await signInAnonymously(authInstance);
            console.log('Firebase Init: signInAnonymously successful. User ID:', result.user.uid);
            
            // Store the anonymous user ID for reference (though we can't resume the session)
            localStorage.setItem('corinthia-anonymous-user-id', result.user.uid);
            console.log('Firebase Init: Anonymous user ID stored for reference.');
          }
        } catch (signInError) {
          console.error("Firebase Init: Auto sign-in failed:", signInError);
          // If auto sign-in fails, we still proceed to allow manual login/signup
          // The onAuthStateChanged listener will handle the null user state.
        }
      };
      signInUser(); // Call the async function immediately

    } catch (initError) {
      console.error("Firebase Init: Error initializing Firebase services with provided config:", initError);
      setFirebaseInitError(true);
      setLoading(false);
      return;
    }

    const unsubscribeAuth = onAuthStateChanged(authInstance, async (user) => {
      console.log('onAuthStateChanged: Callback triggered. User object:', user ? user.uid : 'null (no user)');
      if (user) {
        setUserId(user.uid);
        setUserEmail(user.email);

        // Fetch user role from user_profiles collection
        const userProfileRef = doc(firestoreInstance, `artifacts/${appId}/user_profiles`, user.uid);
        console.log(`onAuthStateChanged: Checking user profile for UID: ${user.uid}`);
        try {
          const userProfileSnap = await getDoc(userProfileRef);
          if (userProfileSnap.exists()) {
            const role = userProfileSnap.data().role || 'user';
            setUserRole(role);
            console.log(`onAuthStateChanged: User profile found. Role: ${role}`);
          } else {
            // If profile doesn't exist, create it (e.g., for new sign-ups)
            await setDoc(userProfileRef, { email: user.email, role: 'user' }, { merge: true });
            setUserRole('user');
            console.log(`onAuthStateChanged: User profile created. Default role: 'user'`);
          }
        } catch (profileError) {
          console.error("onAuthStateChanged: Error fetching/creating user profile:", profileError);
          console.error("Please ensure your Firebase Firestore Security Rules allow authenticated users to read and write their own user profiles. Example rule: match /artifacts/{appId}/user_profiles/{userId} { allow read, write: if request.auth != null && request.auth.uid == userId; }");
          setUserRole('user'); // Fallback to 'user' role on error
        }

        setLoading(false);
        console.log('onAuthStateChanged: User is logged in. userId, userEmail, userRole, loading state updated.');
      } else {
        console.log('onAuthStateChanged: No user logged in. Setting userId to null, loading to false.');
        setUserId(null);
        setUserEmail(null);
        setUserRole(null);
        setLoading(false); // Make sure loading is false so the UI can render the login prompt
      }
    });

    return () => {
      console.log('Firebase Init: Cleanup initiated. Unsubscribing auth listener.');
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    };
  }, []);

  // Effect to automatically open auth modal if not logged in after initial load
  useEffect(() => {
    // Only show modal if loading is complete and no user is authenticated AND the modal is not already open.
    // This prevents flashing or reopening the modal if a user logs out.
    if (!loading && !userId && !showAuthModal) {
      console.log('Auto-opening auth modal: App finished loading, no user, modal not open.');
      setShowAuthModal(true);
    }
  }, [loading, userId, showAuthModal]);

  // Fetch and listen for current user's ticket data
  useEffect(() => {
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    if (!db || !userId) {
      console.log('Firestore Data Fetch (User Tickets): Skipping data fetch. DB ready:', !!db, 'User ID available:', !!userId);
      setTickets([]); // Clear tickets if user logs out or is not authenticated
      return;
    }
    console.log(`Firestore Data Fetch (User Tickets): DB and User ID are ready (${userId}). Starting real-time listener for user-specific tickets.`);

    // Use user-specific collection
    const ticketsCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/tickets`);
    const unsubscribeTickets = onSnapshot(ticketsCollectionRef, (snapshot) => {
      const fetchedTickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Firestore Data Fetch (User Tickets): onSnapshot received data. Total tickets:', fetchedTickets.length, 'Data:', fetchedTickets);
      setTickets(fetchedTickets);
      // updateHotelSales(fetchedTickets); // This will now be handled by a separate effect for overall sales if admin
    }, (error) => {
      console.error("Firestore Data Fetch (User Tickets): Error fetching tickets:", error);
    });

    return () => {
      console.log('Firestore Data Fetch (User Tickets): Cleaning up Firestore listener for user tickets.');
      unsubscribeTickets();
    };
  }, [db, userId]); // Re-run effect when userId or db changes

  // Fetch and listen for ALL ticket data (for all authenticated users on 'track' tab, and for admin tab)
  useEffect(() => {
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    if (!db || !userId) { // Now runs for any authenticated user
      console.log('Firestore Data Fetch (All Tickets): Skipping data fetch. db:', !!db, 'userId:', userId);
      setAllTickets([]); // Clear if not authenticated
      return;
    }
    console.log('Firestore Data Fetch (All Tickets): db and userId are ready. Starting real-time listener for ALL tickets (collectionGroup).');

    // Use collectionGroup to query all 'tickets' subcollections
    const allTicketsQuery = query(collectionGroup(db, 'tickets'));
    const unsubscribeAllTickets = onSnapshot(allTicketsQuery, (snapshot) => {
      const fetchedAllTickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Firestore Data Fetch (All Tickets): All tickets updated. Total:', fetchedAllTickets.length);
      setAllTickets(fetchedAllTickets);
    }, (error) => {
      console.error("Firestore Data Fetch (All Tickets): Error fetching all tickets (collectionGroup view):", error);
      // This is the common place for "Missing or insufficient permissions" if the collectionGroup index is not created or rules are incorrect.
    });

    return () => {
      console.log('Firestore Data Fetch (All Tickets): Cleaning up Firestore listener for all tickets.');
      unsubscribeAllTickets();
    };
  }, [db, userId]); // Now depends on userId, not userRole === 'admin'

  // Effect to calculate current user's hotel sales (used on 'sell' tab)
  useEffect(() => {
    const sales = {};
    const scanned = {};
    HOTELS.forEach(outlet => {
      sales[outlet] = 0;
      scanned[outlet] = 0;
    });

    tickets.forEach(ticket => {
      if (ticket.hotelName && sales[ticket.hotelName] !== undefined) {
        sales[ticket.hotelName]++;
        if (ticket.scanned) {
          scanned[ticket.hotelName]++;
        }
      }
    });
    setHotelSales({ sold: sales, scanned: scanned });
  }, [tickets]); // Recalculate when current user's tickets change

  // Effect to calculate overall hotel sales (used on 'track' tab for all users, and admin tab)
  useEffect(() => {
    const sales = {};
    const scanned = {};
    HOTELS.forEach(outlet => {
      sales[outlet] = 0;
      scanned[outlet] = 0;
    });

    allTickets.forEach(ticket => { // This is the key: it uses allTickets
      if (ticket.hotelName && sales[ticket.hotelName] !== undefined) {
        sales[ticket.hotelName]++;
        if (ticket.scanned) {
          scanned[ticket.hotelName]++;
        }
      }
    });
    setOverallHotelSales({ sold: sales, scanned: scanned });
    console.log('Overall Hotel Sales recalculated:', { sold: sales, scanned: scanned }); // Added log
  }, [allTickets]); // Recalculate when all tickets change


  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!auth) {
      setAuthError('Firebase Authentication not initialized.');
      return;
    }

    try {
      if (isLoginMode) {
        console.log(`Attempting to log in user: ${authEmail}`);
        await signInWithEmailAndPassword(auth, authEmail, authPassword);
        console.log(`User ${authEmail} logged in successfully.`);
      } else {
        console.log(`Attempting to create new user: ${authEmail}`);
        const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        const newUser = userCredential.user;
        // Create user profile document with default role
        const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
        const userProfileRef = doc(db, `artifacts/${appId}/user_profiles`, newUser.uid);
        await setDoc(userProfileRef, { email: newUser.email, role: 'user' }, { merge: true });
        console.log('User profile created for new user:', newUser.uid);
        console.log(`New user ${authEmail} signed up successfully.`);
      }
      setShowAuthModal(false); // Close modal on success
      setAuthEmail('');
      setAuthPassword('');
      // userId and userEmail will be updated by onAuthStateChanged listener
    } catch (error) {
      console.error("Authentication error:", error);
      let errorMessage = "An error occurred.";
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This user account has been disabled.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No user found with this email.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/email-already-in-use':
          errorMessage = 'This email is already in use. Try logging in.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/Password sign-in is not enabled. Please enable it in your Firebase project settings (Authentication -> Sign-in method).';
          console.error("Firebase Auth Error: 'auth/operation-not-allowed'. Please ensure Email/Password sign-in is enabled in your Firebase project settings (Authentication -> Sign-in method).");
          break;
        default:
          errorMessage = `Auth Error: ${error.message}`;
      }
      setAuthError(errorMessage);
    }
  };

  const handleLogout = async () => {
    if (auth) {
      try {
        console.log('Attempting to sign out user.');
        await signOut(auth);
        console.log('User signed out successfully.');
        // userId and userEmail will be cleared by onAuthStateChanged listener
        setUserId(null);
        setUserEmail(null);
        setUserRole(null);
        setTickets([]); // Clear current user's tickets
        setAllTickets([]); // Clear all tickets for admin view
        // Show login modal after logout
        setShowAuthModal(true);
      } catch (error) {
        console.error("Error signing out:", error);
      }
    }
  };

  const handleAddTicket = async (e) => {
    e.preventDefault();
    console.log('handleAddTicket: Function called.');
    setAddTicketStatus(null); // Clear previous status
    setAddTicketMessage(''); // Clear previous message
    setQrToDownload(null);

    // Check if Firebase is ready and user is logged in
    if (!db || !userId) {
      console.error('handleAddTicket: Firebase DB or User ID not ready.', { dbReady: !!db, userIdReady: !!userId });
      setAddTicketStatus('error');
      setAddTicketMessage("Please log in to add tickets.");
      return;
    }
    console.log('handleAddTicket: Firebase DB and User ID are ready.');

    const employeeName = buyerNameRef.current.value.trim();
    const employeeEmail = buyerEmailRef.current.value.trim();
    const employeeMobile = buyerMobileRef.current.value.trim();
    const employeeCompanyId = buyerCompanyIdRef.current.value.trim();
    const hotelName = hotelSelectRef.current.value;

    console.log('handleAddTicket: Input values:', { employeeName, employeeEmail, employeeMobile, employeeCompanyId, hotelName });

    if (!employeeName || !employeeEmail || !hotelName) {
      console.error('handleAddTicket: Missing required input fields (Name, Email, Outlet).');
      setAddTicketStatus('error');
      setAddTicketMessage("Please fill in Employee Name, Employee Email, and Outlet.");
      return;
    }
    console.log('handleAddTicket: Required input fields are filled. Attempting to generate QR and save to Firestore.');


    const qrCodeData = uuidv4();

    try {
      // Use user-specific collection path
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const ticketsCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/tickets`);
      console.log('handleAddTicket: Preparing to add document to Firestore.');
      const docRef = await addDoc(ticketsCollectionRef, {
        buyerName: employeeName,
        buyerEmail: employeeEmail,
        buyerMobile: employeeMobile || null,
        buyerCompanyId: employeeCompanyId || null,
        hotelName,
        qrCodeData,
        purchaseDate: new Date().toISOString(),
        scanned: false,
        recordedBy: userId,
        recordedByEmail: userEmail
      });
      console.log('handleAddTicket: Document successfully added to Firestore, ID:', docRef.id);

      // Reset form fields
      if (buyerNameRef.current) buyerNameRef.current.value = '';
      if (buyerEmailRef.current) buyerEmailRef.current.value = '';
      if (buyerMobileRef.current) buyerMobileRef.current.value = '';
      if (buyerCompanyIdRef.current) buyerCompanyIdRef.current.value = '';
      if (hotelSelectRef.current) hotelSelectRef.current.value = '';

      // Show success message and QR modal
      setQrToDownload(qrCodeData);
      setShowAddTicketModal(false);
      setShowQrDisplayModal(true);
      setAddTicketMessage(`Ticket added for ${employeeName}!`);
      setAddTicketStatus('success');
      setLastAddedTicketId(docRef.id);
      console.log('handleAddTicket: Post-add actions completed successfully.');

    } catch (error) {
      console.error("handleAddTicket: Error adding ticket to Firestore:", error);
      setAddTicketStatus('error');
      setAddTicketMessage(`Failed to add ticket: ${error.message || 'Unknown error'}. Please try again.`);
    }
  };

  const handleOpenQrDownloadModal = (qrData) => {
    setQrToDownload(qrData);
    setShowQrDisplayModal(true);
    setAddTicketMessage('');
    setAddTicketStatus(null);
  };

  const handleDownloadQrCode = () => {
    const canvas = document.getElementById('qr-display-modal-canvas');
    if (canvas) {
      const imageUrl = canvas.toDataURL("image/png");
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `corinthia_party_ticket_${qrToDownload}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.error("QR Code Canvas element not found for download.");
    }
  };

  // Function to start the camera scanner using html5-qrcode
  const startScanner = async () => {
    if (!html5QrScannerLoaded) {
      setScanCameraError('QR scanner library not loaded yet. Please wait.');
      console.error('html5-qrcode library not loaded when attempting to start scanner.');
      return;
    }
    console.log('html5-qrcode library is loaded. Attempting to start camera.');
    setScanCameraError('');
    setScanStatus(null);
    setScanMessage('');
    setIsScanning(true);
    setIsProcessingQr(false); // Ensure this is false when starting a new scan

    // Wait for the DOM to update with the qr-reader div
    setTimeout(async () => {
      try {
        // Stop any existing scanner
        if (html5QrScannerRef.current) {
          try {
            await html5QrScannerRef.current.stop();
            html5QrScannerRef.current.clear();
          } catch (e) {
            console.log('No active scanner to stop');
          }
        }

        // Check if the element exists
        const qrReaderElement = document.getElementById('qr-reader');
        if (!qrReaderElement) {
          throw new Error('QR reader element not found in DOM');
        }

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          facingMode: "environment" // Use back camera
        };

        const html5QrScanner = new Html5QrcodeScanner(
          "qr-reader",
          config,
          /* verbose= */ false
        );

        html5QrScannerRef.current = html5QrScanner;

        html5QrScanner.render(
          (qrCodeMessage) => {
            // Success callback
            console.log("QR Code detected:", qrCodeMessage);
            if (!isProcessingQr) {
              setIsProcessingQr(true);
              // Stop the scanner immediately when QR is detected to prevent multiple scans
              html5QrScanner.pause(true);
              handleScanResult(qrCodeMessage);
            }
          },
          (errorMessage) => {
            // Error callback - most of these are just "no QR code found" which is normal
            // Only log actual errors, not scanning attempts
            if (errorMessage && !errorMessage.includes('No QR code found')) {
              console.log('QR Scanner error:', errorMessage);
            }
          }
        );

      } catch (err) {
        setIsScanning(false);
        setIsProcessingQr(false);
        console.error("Error starting QR scanner:", err);
        if (err.name === 'NotAllowedError') {
          setScanCameraError('Camera access denied. Please allow camera permissions in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setScanCameraError('No camera found on this device.');
        } else {
          setScanCameraError(`Error starting scanner: ${err.message || 'Unknown error'}`);
        }
      }
    }, 100); // Wait 100ms for DOM to update
  };

  // Function to stop the camera scanner
  const stopScanner = async () => {
    if (html5QrScannerRef.current) {
      try {
        await html5QrScannerRef.current.stop();
        html5QrScannerRef.current.clear();
        html5QrScannerRef.current = null;
        console.log('Scanner successfully stopped and cleared.');
      } catch (error) {
        console.log('Error stopping scanner (may already be stopped):', error);
      }
    }
    setIsScanning(false); // Set state AFTER attempting to stop
    setIsProcessingQr(false);
    setScanCameraError('');
  };

  // Cleanup effect for camera scanner when component unmounts
  useEffect(() => {
    return () => {
      // This cleanup runs when the App component unmounts
      if (html5QrScannerRef.current) {
        html5QrScannerRef.current.stop().catch(console.error);
        html5QrScannerRef.current.clear();
        html5QrScannerRef.current = null;
      }
    };
  }, []); // Empty dependency array means it runs once on mount, and cleanup on unmount


  const handleScanResult = async (codeToScan) => {
    setScanStatus(null);
    setScanMessage('');
    setScanCameraError('');

    if (!db || !userId) {
      setScanStatus('error');
      setScanMessage("Application not fully loaded or not authenticated. Please log in.");
      console.error("Attempted to scan ticket before Firebase was ready or user was authenticated.");
      setIsProcessingQr(false);
      if (html5QrScannerRef.current) {
        html5QrScannerRef.current.resume();
      }
      return;
    }

    if (!codeToScan) {
      setScanStatus('error');
      setScanMessage("No QR code data provided for scanning.");
      setIsProcessingQr(false);
      if (html5QrScannerRef.current) {
        html5QrScannerRef.current.resume();
      }
      return;
    }

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      let q;
      // For scanning, we always use collectionGroup to find the ticket regardless of who owns it
      // because the permission check happens *after* finding the ticket.
      console.log(`User ${userId} (Role: ${userRole}) attempting to scan code: ${codeToScan} using collectionGroup.`);
      q = query(collectionGroup(db, 'tickets'), where("qrCodeData", "==", codeToScan));
      
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setScanStatus('error');
        setScanMessage("Ticket not found. Invalid QR code.");
        console.log(`Scan failed: Ticket with QR data ${codeToScan} not found.`);
        setIsProcessingQr(false);
        if (html5QrScannerRef.current) {
          html5QrScannerRef.current.resume();
        }
      } else {
        const ticketDoc = querySnapshot.docs[0];
        const ticketData = ticketDoc.data();
        console.log(`Ticket found: ${ticketDoc.id}, Data:`, ticketData);

        // --- Permission Check for Scanning ---
        // Only an admin OR the original recorder of the ticket can scan it.
        if (userRole !== 'admin' && ticketData.recordedBy !== userId) {
          setScanStatus('error');
          setScanMessage("You do not have permission to scan this ticket.");
          console.warn(`Permission Denied: User ${userId} (Role: ${userRole}) attempted to scan ticket ${ticketDoc.id} owned by ${ticketData.recordedBy}.`);
          setIsProcessingQr(false);
          if (html5QrScannerRef.current) {
            html5QrScannerRef.current.resume();
          }
          return;
        }
        // --- End Permission Check ---

        if (ticketData.scanned) {
          setScanStatus('info');
          setScanMessage(`Ticket for ${ticketData.buyerName} from ${ticketData.hotelName} has already been scanned.`);
          console.log(`Scan info: Ticket ${ticketDoc.id} already scanned.`);
          stopScanner();
        } else {
          const ticketToUpdateRef = doc(db, ticketDoc.ref.path);

          console.log(`Updating ticket ${ticketDoc.id} to scanned with ref path ${ticketDoc.ref.path}`);
          await updateDoc(ticketToUpdateRef, {
            scanned: true,
            scanDate: new Date().toISOString(),
            scannedBy: userId,
            scannedByEmail: userEmail
          });
          setScanStatus('success');
          setScanMessage(`Ticket for ${ticketData.buyerName} from ${ticketData.hotelName} scanned successfully!`);
          console.log(`Scan successful: Ticket ${ticketDoc.id} marked as scanned.`);
          stopScanner();
        }
      }
    } catch (error) {
      console.error("Error processing scanned QR code:", error);
      let errorMessage = "An error occurred during scanning. Please try again.";
      if (error.code === 'failed-precondition' && error.message.includes('The query requires an index')) {
        errorMessage = `Scanning failed: A required Firestore index is missing. Please check your Firebase console (Firestore -> Indexes) and create the suggested index.`;
        console.error("Firestore Index Missing Error:", error.message);
      } else if (error.code === 'permission-denied') {
        errorMessage = `Scanning failed: Permission denied. Please check your Firebase Firestore Security Rules.`;
        console.error("Firestore Permission Denied Error:", error.message);
      }
      setScanStatus('error');
      setScanMessage(errorMessage);
      setIsProcessingQr(false);
      if (html5QrScannerRef.current) {
        html5QrScannerRef.current.resume();
      }
    } finally {
      setManualQrInput('');
      setTimeout(() => setIsProcessingQr(false), 1000);
    }
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    // Explicitly stop camera scanning if switching away from the scan tab
    if (tabName !== 'scan') {
      stopScanner();
    } else {
      // If switching TO scan tab, start scanner immediately
      startScanner();
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    selectedViewHotel === 'All Outlets' || ticket.hotelName === selectedViewHotel
  );

  const filteredAllTickets = allTickets.filter(ticket =>
    selectedAdminViewHotel === 'All Outlets' || ticket.hotelName === selectedAdminViewHotel
  );

  const handlePrintReport = (reportType, dataToPrint) => {
    const reportWindow = window.open('', '_blank', 'width=800,height=600');
    if (!reportWindow) {
      console.error('Please allow pop-ups for this website to print the report.');
      return;
    }

    let reportTitle = "Corinthia Summer Party Ticket Sales Report";
    let reportSubtitle = `Report Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
    let reportContentHtml = '';

    // If dataToPrint is 'all', use allTickets. Otherwise, use personal tickets.
    // userRole === 'admin' check is removed here because allTickets is now always available if authenticated.
    const ticketsForReport = (dataToPrint === 'all') ? allTickets : tickets;
    const totalSold = ticketsForReport.length;
    const totalScanned = ticketsForReport.filter(ticket => ticket.scanned).length;

    if (reportType === 'sales_summary') {
      const salesData = {};
      const scannedData = {};
      HOTELS.forEach(outlet => {
        salesData[outlet] = 0;
        scannedData[outlet] = 0;
      });
      ticketsForReport.forEach(ticket => {
        if (ticket.hotelName && salesData[ticket.hotelName] !== undefined) {
          salesData[ticket.hotelName]++;
          if (ticket.scanned) {
            scannedData[ticket.hotelName]++;
          }
        }
      });

      if (dataToPrint === 'user' && selectedViewHotel !== 'All Outlets') {
        reportTitle += ` - ${selectedViewHotel}`;
      } else if (dataToPrint === 'all') {
        reportTitle = `Corinthia Summer Party Master Sales Report`;
      }


      reportContentHtml = `
        <h3 style="font-size: 1.5rem; font-weight: bold; margin-top: 20px; margin-bottom: 15px; color: #101820;">Sales Overview by Outlet</h3>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px;">
          ${HOTELS.map(outlet => `
            <div style="background-color: #D9D9D6; border-radius: 8px; padding: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border: 1px solid #AFA9A0;">
              <h4 style="font-size: 1.25rem; font-weight: bold; color: #101820; margin-bottom: 8px;">${outlet}</h4>
              <p style="font-size: 2rem; font-weight: bolder; color: #FFA300;">${salesData[outlet] !== undefined ? salesData[outlet] : 0}</p>
              <p style="color: #AFA9A0; margin-top: 4px;">Tickets Sold</p>
              <p style="font-size: 1.5rem; font-weight: bolder; color: #10B981; margin-top: 10px;">${scannedData[outlet] !== undefined ? scannedData[outlet] : 0}</p>
              <p style="color: #AFA9A0; margin-top: 4px;">Tickets Scanned</p>
            </div>
          `).join('')}
        </div>
        <p style="font-size: 1.25rem; font-weight: bold; text-align: center; background-color: #FFA300; color: #101820; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          Total Tickets Sold Across All Outlets: ${totalSold} <br/>
          Total Tickets Scanned Across All Outlets: ${totalScanned}
        </p>

        <h3 style="font-size: 1.5rem; font-weight: bold; margin-top: 30px; margin-bottom: 15px; color: #101820;">Detailed Ticket Sales</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
          <thead style="background-color: #D7D2CB;">
            <tr>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Employee Name</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Employee Email</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Mobile</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Company ID</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Outlet</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Scanned</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Purchase Date</th>
              <th style="padding: 8px 16px; text-align: left; font-size: 0.75rem; font-weight: 500; color: #101820; text-transform: uppercase;">Recorded By</th>
            </tr>
          </thead>
          <tbody>
            ${ticketsForReport.length === 0 ? `
              <tr><td colSpan="8" style="padding: 20px; text-align: center; color: #AFA9A0;">No tickets found for this view.</td></tr>
            ` : ticketsForReport.map(ticket => `
              <tr>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #101820;">${ticket.buyerName}</td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #101820;">${ticket.buyerEmail}</td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #101820;">${ticket.buyerMobile || 'N/A'}</td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #101820;">${ticket.buyerCompanyId || 'N/A'}</td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #FFA300; font-weight: 600;">${ticket.hotelName}</td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: ${ticket.scanned ? '#065F46' : '#991B1B'};">
                  ${ticket.scanned ? 'Yes' : 'No'}
                </td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #AFA9A0;">
                  ${new Date(ticket.purchaseDate).toLocaleDateString()}
                </td>
                <td style="padding: 8px 16px; font-size: 0.875rem; color: #AFA9A0;">
                  ${ticket.recordedByEmail || ticket.recordedBy || 'N/A'}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else if (reportType === 'attendee_list') {
      reportTitle = `Corinthia Summer Party Attendee List ${dataToPrint === 'all' ? ' (All Users)' : ''}`;

      const attendeesByOutlet = {};
      HOTELS.concat('Other').forEach(outlet => {
        attendeesByOutlet[outlet] = [];
      });

      ticketsForReport.forEach(ticket => {
        const outlet = ticket.hotelName && HOTELS.includes(ticket.hotelName) ? ticket.hotelName : 'Other';
        attendeesByOutlet[outlet].push(ticket.buyerName);
      });

      reportContentHtml = `
        <h3 style="font-size: 1.5rem; font-weight: bold; margin-top: 20px; margin-bottom: 15px; color: #101820;">Attendees Grouped by Outlet</h3>
        ${HOTELS.concat('Other').filter(outlet => attendeesByOutlet[outlet].length > 0).map(outlet => {
        const sortedNames = attendeesByOutlet[outlet].sort((a, b) => a.localeCompare(b));
        return `
            <div style="margin-bottom: 30px; padding: 15px; background-color: #D9D9D6; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border: 1px solid #AFA9A0;">
              <h4 style="font-size: 1.25rem; font-weight: bold; color: #101820; margin-bottom: 10px;">${outlet} (${sortedNames.length} attendees)</h4>
              <ul style="list-style-type: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 10px;">
                ${sortedNames.map(name => `
                  <li style="background-color: #D7D2CB; padding: 8px 12px; border-radius: 5px; font-size: 0.95rem; color: #101820; flex: 0 0 auto; min-width: 150px;">${name}</li>
                `).join('')}
              </ul>
            </div>
          `;
      }).join('')}
      `;
    }

    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body { font-family: 'Inter', sans-serif; margin: 20px; color: #333; line-height: 1.6; }
          h1 { color: #101820; text-align: center; margin-bottom: 10px; }
          h2 { text-align: center; color: #AFA9A0; margin-bottom: 30px; font-weight: normal;}
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #AFA9A0; padding: 8px; text-align: left; }
          th { background-color: #D7D2CB; }
          ul { padding-left: 0; list-style: none; display: flex; flex-wrap: wrap; gap: 10px; }
          li { margin-bottom: 5px; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
      </head>
      <body>
        <h1>${reportTitle}</h1>
        <h2>${reportSubtitle}</h2>
        ${reportContentHtml}
      </body>
      </html>
    `;

    reportWindow.document.write(fullHtml);
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.onload = function () {
      reportWindow.print();
    };
  };

  const handleExportCsv = (dataToExport) => {
    // If dataToExport is 'all', use allTickets. Otherwise, use personal tickets.
    const ticketsForExport = (dataToExport === 'all') ? allTickets : tickets;

    if (ticketsForExport.length === 0) {
      console.error('No ticket sales data to export.');
      return;
    }

    const headers = [
      "Employee Name",
      "Employee Email",
      "Mobile Number",
      "Company ID/ID Card",
      "Outlet",
      "Scanned Status",
      "Purchase Date",
      "QR Code Data",
      "Recorded By User ID",
      "Recorded By Email",
      "Scanned By User ID",
      "Scanned By Email"
    ];

    const csvRows = ticketsForExport.map(ticket => {
      const scannedStatus = ticket.scanned ? "Yes" : "No";
      const purchaseDate = new Date(ticket.purchaseDate).toLocaleDateString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });

      const escapeCsvField = (field) => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      return [
        escapeCsvField(ticket.buyerName),
        escapeCsvField(ticket.buyerEmail),
        escapeCsvField(ticket.buyerMobile || ''),
        escapeCsvField(ticket.buyerCompanyId || ''),
        escapeCsvField(ticket.hotelName),
        escapeCsvField(scannedStatus),
        escapeCsvField(purchaseDate),
        escapeCsvField(ticket.qrCodeData),
        escapeCsvField(ticket.recordedBy || ''),
        escapeCsvField(ticket.recordedByEmail || ''),
        escapeCsvField(ticket.scannedBy || ''),
        escapeCsvField(ticket.scannedByEmail || '')
      ].join(',');
    });

    const csvContent = [
      headers.join(','),
      ...csvRows
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `Corinthia_Ticket_Sales_Report_${dataToExport === 'all' ? 'All_' : ''}${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewLastEntry = async () => {
    if (!lastAddedTicketId || !db || !userId) {
      setLastEntryEditStatus('error');
      setLastEntryEditMessage("Please log in to view last entry.");
      console.error("Attempted to view last entry before Firebase was ready or user was authenticated.");
      return;
    }

    setLastEntryEditStatus(null);
    setLastEntryEditMessage('');

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const ticketDocRef = doc(db, `artifacts/${appId}/users/${userId}/tickets`, lastAddedTicketId);
      const ticketSnap = await getDoc(ticketDocRef);

      if (ticketSnap.exists()) {
        const data = ticketSnap.data();
        setLastEntryTicketDetails({ id: ticketSnap.id, ...data });
        setEditingBuyerName(data.buyerName || '');
        setEditingBuyerEmail(data.buyerEmail || '');
        setEditingBuyerMobile(data.buyerMobile || '');
        setEditingBuyerCompanyId(data.buyerCompanyId || '');
        setEditingHotelName(data.hotelName || '');
        setShowLastEntryModal(true);
      } else {
        setLastEntryEditStatus('error');
        setLastEntryEditMessage("Last entry not found in your tickets.");
        setLastAddedTicketId(null);
      }
    } catch (error) {
      console.error("Error fetching last entry:", error);
      setLastEntryEditStatus('error');
      setLastEntryEditMessage("Failed to load last entry. Please try again.");
    }
  };

  const handleUpdateLastEntry = async (e) => {
    e.preventDefault();
    setLastEntryEditStatus(null);
    setLastEntryEditMessage('');

    if (!lastEntryTicketDetails || !db || !userId) {
      setLastEntryEditStatus('error');
      setLastEntryEditMessage("Application not fully loaded or no ticket selected for update. Please log in.");
      console.error("Attempted to update last entry before Firebase was ready or user was authenticated.");
      return;
    }

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const ticketDocRef = doc(db, `artifacts/${appId}/users/${userId}/tickets`, lastEntryTicketDetails.id);
      await updateDoc(ticketDocRef, {
        buyerName: editingBuyerName.trim(),
        buyerEmail: editingBuyerEmail.trim(),
        buyerMobile: editingBuyerMobile.trim() || null,
        buyerCompanyId: editingBuyerCompanyId.trim() || null,
        hotelName: editingHotelName
      });
      setLastEntryEditStatus('success');
      setLastEntryEditMessage("Ticket details updated successfully!");
      setTimeout(() => setShowLastEntryModal(false), 1500);
    } catch (error) {
      console.error("Error updating last entry:", error);
      setLastEntryEditStatus('error');
      setLastEntryEditMessage("Failed to update ticket details. Please try again.");
    }
  };

  const handleOpenDeleteModal = (ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteConfirmModal(true);
    setDeletePasswordInput('');
    setDeleteError('');
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    setDeleteError('');

    if (!db || !userId) {
      setDeleteError("Application not fully loaded or not authenticated. Please log in.");
      console.error("Attempted to delete ticket before Firebase was ready or user was authenticated.");
      return;
    }

    if (deletePasswordInput !== RESTRICTED_PASSWORD) {
      setDeleteError('Incorrect password. Deletion cancelled.');
      return;
    }

    if (!ticketToDelete) {
      setDeleteError('No ticket selected for deletion.');
      return;
    }

    try {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      const ticketOwnerId = ticketToDelete.recordedBy;

      if (userRole === 'admin' || ticketOwnerId === userId) {
        await deleteDoc(doc(db, `artifacts/${appId}/users/${ticketOwnerId}/tickets`, ticketToDelete.id));
        console.log(`Ticket ${ticketToDelete.id} deleted successfully.`);
        setShowDeleteConfirmModal(false);
        setTicketToDelete(null);
        setAddTicketMessage('Ticket deleted successfully!');
        setAddTicketStatus('success');
        setTimeout(() => setAddTicketMessage(''), 3000);

        if (lastAddedTicketId === ticketToDelete.id) {
          setLastAddedTicketId(null);
        }
      } else {
        setDeleteError("You do not have permission to delete this ticket.");
        console.warn(`User ${userId} attempted to delete ticket ${ticketToDelete.id} owned by ${ticketOwnerId} without admin role.`);
      }

    } catch (error) {
      console.error("Error deleting ticket:", error);
      setDeleteError(`Failed to delete ticket: ${error.message}`);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="text-xl text-gray-700">Loading application...</div>
      </div>
    );
  }

  if (firebaseInitError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50 p-4 text-center">
        <div className="text-xl text-red-700">
          <p className="font-bold mb-2">Error: Firebase Configuration Missing or Invalid!</p>
          <p className="mb-4">
            The application cannot connect to Firebase because the configuration is either not provided or is incorrect.
          </p>
          <p>
            Please ensure you have replaced the `YOUR_...` placeholders in the `firebaseConfig` object within `App.js`
            with your actual Firebase project settings for your deployed application.
          </p>
          <p className="text-sm text-red-500 mt-4">
            Check the browser console for more detailed error messages from Firebase.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#D9D9D6] to-[#D7D2CB] font-inter text-[#101820] p-4 sm:p-6 lg:p-8 flex flex-col items-center">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl p-6 sm:p-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-center text-[#101820] mb-6 border-b-2 pb-4 border-[#AFA9A0]">
          Corinthia Summer Party
        </h1>

        <div className="text-center text-sm text-[#AFA9A0] mb-4 flex items-center justify-center p-2 bg-[#D9D9D6] rounded-lg">
          <User className="w-4 h-4 mr-2 text-[#FFA300]" />
          <p>
            {userEmail ? (
              <>Logged in as: <span className="font-mono text-[#101820] break-words">{userEmail}</span></>
            ) : (
              <>Your User ID: <span className="font-mono text-[#101820] break-words">{userId || "N/A"}</span></>
            )}
            {userRole && <span className="ml-2 font-bold text-[#FFA300] capitalize">({userRole})</span>}
          </p>
        </div>

        {/* Warning for anonymous users about cross-device limitations */}
        {!userEmail && userId && (
          <div className="text-center text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-yellow-800">
              <strong>⚠️ Cross-Device Note:</strong> You're using anonymous authentication. 
              Your tickets are tied to this device/browser session. To access your tickets on other devices, 
              <button 
                onClick={() => setShowAuthModal(true)}
                className="text-[#FFA300] hover:underline ml-1 font-semibold"
              >
                create an account with email
              </button>.
            </p>
          </div>
        )}

        <div className="flex justify-center mb-8 border-b border-[#AFA9A0] overflow-x-auto">
          {userId ? (
            <>
              <button
                className={`flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 ${activeTab === 'sell' ? 'bg-[#FFA300] text-[#101820] shadow-md' : 'text-[#AFA9A0] hover:bg-[#D9D9D6]'}`}
                onClick={() => handleTabChange('sell')}
              >
                <Plus className="inline-block w-5 h-5 mr-2" /> Tickets
              </button>
              <button
                className={`flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 ${activeTab === 'track' ? 'bg-[#FFA300] text-[#101820] shadow-md' : 'text-[#AFA9A0] hover:bg-[#D9D9D6]'}`}
                onClick={() => handleTabChange('track')}
              >
                <BarChart2 className="inline-block w-5 h-5 mr-2" /> Track Sales
              </button>
              <button
                className={`flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 ${activeTab === 'scan' ? 'bg-[#FFA300] text-[#101820] shadow-md' : 'text-[#AFA9A0] hover:bg-[#D9D9D6]'}`}
                onClick={() => handleTabChange('scan')}
              >
                <Search className="inline-block w-5 h-5 mr-2" /> Scan QR
              </button>
              {userRole === 'admin' && (
                <button
                  className={`flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 ${activeTab === 'admin' ? 'bg-[#FFA300] text-[#101820] shadow-md' : 'text-[#AFA9A0] hover:bg-[#D9D9D6]'}`}
                  onClick={() => handleTabChange('admin')}
                >
                  <Settings className="inline-block w-5 h-5 mr-2" /> Admin Dashboard
                </button>
              )}
              {!userEmail && (
                <button
                  className="flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 text-[#FFA300] hover:bg-[#D9D9D6] border-2 border-[#FFA300] ml-4"
                  onClick={() => setShowAuthModal(true)}
                >
                  <User className="inline-block w-5 h-5 mr-2" /> Create Email Account
                </button>
              )}
              <button
                className="flex-shrink-0 py-3 px-4 text-md sm:text-lg font-semibold rounded-t-lg transition-all duration-300 text-[#AFA9A0] hover:bg-[#D9D9D6] ml-4"
                onClick={handleLogout}
              >
                <LogOut className="inline-block w-5 h-5 mr-2" /> Logout
              </button>
            </>
          ) : (
            null
          )}
        </div>

        {/* Main Content Area */}
        {userId ? (
          <>
            {activeTab === 'sell' && (
              <div className="animate-fade-in">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                  <button
                    onClick={() => { setShowAddTicketModal(true); setAddTicketStatus(null); setAddTicketMessage(''); setQrToDownload(null); }}
                    className="w-full sm:w-1/2 bg-[#FFA300] hover:bg-[#e69400] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center transition duration-300"
                  >
                    <Plus className="w-6 h-6 mr-2" /> Add New Ticket Sale
                  </button>
                  <button
                    onClick={handleViewLastEntry}
                    disabled={!lastAddedTicketId}
                    className={`w-full sm:w-1/2 bg-[#AFA9A0] hover:bg-[#969088] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center transition duration-300 ${!lastAddedTicketId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Eye className="w-6 h-6 mr-2" /> View Last Entry
                  </button>
                </div>

                {addTicketStatus === 'success' && addTicketMessage && (
                  <div className="bg-green-100 text-green-800 p-3 rounded-lg flex items-center mb-4">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    <p className="font-semibold">{addTicketMessage}</p>
                  </div>
                )}
                {addTicketStatus === 'error' && addTicketMessage && (
                  <div className="bg-red-100 text-red-800 p-3 rounded-lg flex items-center mb-4">
                    <XCircle className="w-5 h-5 mr-2" />
                    <p className="font-semibold">{addTicketMessage}</p>
                  </div>
                )}

                <h2 className="text-2xl font-semibold text-[#101820] mb-4 flex items-center">
                  <CreditCard className="w-6 h-6 mr-3 text-[#FFA300]" /> My Sold Tickets
                </h2>

                <div className="mb-4 flex items-center">
                  <label htmlFor="hotelViewSelect" className="block text-[#101820] text-sm font-semibold mr-2">
                    View Tickets For:
                  </label>
                  <select
                    id="hotelViewSelect"
                    value={selectedViewHotel}
                    onChange={(e) => setSelectedViewHotel(e.target.value)}
                    className="p-2 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200 bg-white"
                  >
                    <option value="All Outlets">All Outlets</option>
                    {HOTELS.map((outlet) => (
                      <option key={`view-${outlet}`} value={outlet}>{outlet}</option>
                    ))}
                  </select>
                </div>

                <div className="bg-[#D9D9D6] p-4 rounded-lg shadow-inner overflow-x-auto">
                  {filteredTickets.length === 0 ? (
                    <p className="text-center text-[#AFA9A0] py-8">No tickets found for selected view.</p>
                  ) : (
                    <table className="min-w-full divide-y divide-[#AFA9A0]">
                      <thead className="bg-[#D7D2CB]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider rounded-tl-lg">Employee Name</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Employee Email</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Mobile</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Company ID</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Outlet</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">QR Code</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Scanned</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Purchase Date</th><th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-[#D7D2CB]">
                        {filteredTickets.map((ticket) => (
                          <tr key={ticket.id} className="hover:bg-[#D9D9D6]">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#101820]">{ticket.buyerName}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#101820]">{ticket.buyerEmail}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#101820]">{ticket.buyerMobile || 'N/A'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#101820]">{ticket.buyerCompanyId || 'N/A'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#FFA300] font-semibold">{ticket.hotelName}</td>
                            <td className="px-4 py-3">
                              {qriousLoaded && ticket.qrCodeData ? (
                                <QRCodeCanvas value={ticket.qrCodeData} size={64} id={`qr-${ticket.id}`} />
                              ) : (
                                <span className="text-xs text-[#AFA9A0]">Loading QR...</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {ticket.scanned ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-4 h-4 mr-1" /> Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle className="w-4 h-4 mr-1" /> No
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#AFA9A0]">
                              {new Date(ticket.purchaseDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleOpenQrDownloadModal(ticket.qrCodeData)}
                                className="text-[#AFA9A0] hover:text-[#101820] ml-2 focus:outline-none focus:ring-2 focus:ring-[#FFA300] focus:ring-opacity-50 rounded-full p-1 transition duration-150 ease-in-out"
                                title="Download QR Code"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleOpenDeleteModal(ticket)}
                                className="text-red-600 hover:text-red-900 ml-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 rounded-full p-1 transition duration-150 ease-in-out"
                                title="Delete Ticket"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'track' && (
              <div className="animate-fade-in">
                <>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                    <h2 className="text-2xl font-semibold text-[#101820] flex items-center">
                      <BarChart2 className="w-6 h-6 mr-3 text-[#FFA300]" />
                      Overall Sales Overview by Outlet {/* Now always "Overall" for all users */}
                    </h2>
                    <div className="flex gap-2 flex-wrap justify-center sm:justify-end">
                      {/* Always pass 'all' for track page */}
                      <button
                        onClick={() => handlePrintReport('sales_summary', 'all')}
                        className="bg-[#AFA9A0] hover:bg-[#969088] text-[#101820] font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                      >
                        <Printer className="w-4 h-4 mr-2" /> Print Sales Summary
                      </button>
                      {/* Always pass 'all' for track page */}
                      <button
                        onClick={() => handlePrintReport('attendee_list', 'all')}
                        className="bg-[#D7D2CB] hover:bg-[#c4bfb8] text-[#101820] font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                      >
                        <Printer className="w-4 h-4 mr-2" /> Print Attendee List
                      </button>
                      {/* Always pass 'all' for track page */}
                      <button
                        onClick={() => handleExportCsv('all')}
                        className="bg-[#101820] hover:bg-[#2c3742] text-white font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                      >
                        <FileText className="w-4 h-4 mr-2" /> Export to CSV
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {HOTELS.map((outlet) => (
                      <div key={outlet} className="bg-[#D9D9D6] rounded-lg p-6 shadow-md border border-[#AFA9A0]">
                        <h3 className="text-xl font-bold text-[#101820] mb-2 flex items-center">
                          <Hotel className="w-5 h-5 mr-2 text-[#FFA300]" /> {outlet}
                        </h3>
                        <p className="text-3xl font-extrabold text-[#FFA300]">
                          {overallHotelSales.sold[outlet] || 0} {/* Always uses overallHotelSales */}
                        </p>
                        <p className="text-[#AFA9A0] mt-1">Tickets Sold</p>
                        <p className="text-2xl font-extrabold text-black-600 mt-4">
                          {overallHotelSales.scanned[outlet] || 0} {/* Always uses overallHotelSales */}
                        </p>
                        <p className="text-[#AFA9A0] mt-1">Tickets Scanned</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 text-center text-lg font-bold text-[#101820] bg-[#D7D2CB] p-4 rounded-lg shadow-inner">
                    Total Tickets Sold: {allTickets.length} <br /> {/* Always uses allTickets */}
                    Total Tickets Scanned: {allTickets.filter(ticket => ticket.scanned).length} {/* Always uses allTickets */}
                  </div>
                  <p className="text-center text-sm text-blue-700 mt-4">
                    Note: All authenticated users can view overall ticket sales data.
                  </p>
                </>
              </div>
            )}

            {activeTab === 'scan' && (
              <div className="animate-fade-in">
                <div className="flex flex-col items-center justify-center p-4 bg-[#D9D9D6] rounded-lg shadow-md">
                  <h2 className="text-2xl font-semibold text-[#101820] mb-4 flex items-center">
                    <Search className="w-6 h-6 mr-3 text-[#FFA300]" /> Scan Ticket QR Code
                  </h2>
                  <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-inner mb-6">
                    {isScanning ? (
                      <>
                        <p className="text-center text-[#AFA9A0] mb-4">Scanning for QR code...</p>
                        <div id="qr-reader" className="w-full h-auto max-w-full aspect-square bg-gray-200 rounded-lg overflow-hidden"></div>
                        {scanCameraError && (
                          <p className="text-red-600 text-center mt-4">{scanCameraError}</p>
                        )}
                        <button
                          onClick={stopScanner}
                          className="mt-4 w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-md flex items-center justify-center transition duration-300 transform hover:scale-105"
                        >
                          <Camera className="w-6 h-6 mr-2" /> Stop Camera
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={startScanner}
                        className="w-full bg-[#FFA300] hover:bg-[#e69400] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center transition duration-300 transform hover:scale-105"
                      >
                        <Camera className="w-6 h-6 mr-2" /> Start Camera Scan
                      </button>
                    )}
                  </div>

                  <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-inner">
                    <h3 className="text-xl font-semibold text-[#101820] mb-4">Manual QR Code Entry</h3>
                    <form onSubmit={(e) => { e.preventDefault(); handleScanResult(manualQrInput); }} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Enter QR Code Data Manually"
                        value={manualQrInput}
                        onChange={(e) => setManualQrInput(e.target.value)}
                        className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                        required
                      />
                      <button
                        type="submit"
                        className="w-full bg-[#AFA9A0] hover:bg-[#969088] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-md flex items-center justify-center transition duration-300 transform hover:scale-105"
                      >
                        <Search className="w-6 h-6 mr-2" /> Scan Manually
                      </button>
                    </form>
                  </div>

                  {scanStatus && scanMessage && (
                    <div className={`mt-6 p-4 rounded-lg w-full max-w-md text-center ${scanStatus === 'success' ? 'bg-green-100 text-green-800' : scanStatus === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                      <p className="font-semibold">{scanMessage}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'admin' && userRole === 'admin' && (
              <div className="animate-fade-in">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                  <h2 className="text-2xl font-semibold text-[#101820] flex items-center">
                    <Settings className="w-6 h-6 mr-3 text-[#FFA300]" /> Admin Dashboard - All Tickets
                  </h2>
                  <div className="flex gap-2 flex-wrap justify-center sm:justify-end">
                    <button
                      onClick={() => handlePrintReport('sales_summary', 'all')}
                      className="bg-[#AFA9A0] hover:bg-[#969088] text-[#101820] font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print All Sales Summary
                    </button>
                    <button
                      onClick={() => handlePrintReport('attendee_list', 'all')}
                      className="bg-[#D7D2CB] hover:bg-[#c4bfb8] text-[#101820] font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print All Attendee List
                    </button>
                    <button
                      onClick={() => handleExportCsv('all')}
                      className="bg-[#101820] hover:bg-[#2c3742] text-white font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105 text-sm"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Export All to CSV
                    </button>
                  </div>
                </div>

                <div className="mb-4 flex items-center">
                  <label htmlFor="adminHotelViewSelect" className="block text-[#101820] text-sm font-semibold mr-2">
                    View Tickets For:
                  </label>
                  <select
                    id="adminHotelViewSelect"
                    value={selectedAdminViewHotel}
                    onChange={(e) => setSelectedAdminViewHotel(e.target.value)}
                    className="p-2 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200 bg-white"
                  >
                    <option value="All Outlets">All Outlets</option>
                    {HOTELS.map((outlet) => (
                      <option key={`admin-view-${outlet}`} value={outlet}>{outlet}</option>
                    ))}
                  </select>
                </div>

                <div className="bg-[#D9D9D6] p-4 rounded-lg shadow-inner overflow-x-auto">
                  {filteredAllTickets.length === 0 ? (
                    <p className="text-center text-[#AFA9A0] py-8">No tickets found in the entire system.</p>
                  ) : (
                    <table className="min-w-full divide-y divide-[#AFA9A0]">
                      <thead className="bg-[#D7D2CB]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider rounded-tl-lg">Employee Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Employee Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Outlet</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">QR Code</th> {/* Added QR Code Header */}
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Scanned</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Purchase Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Recorded By</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#101820] uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-[#D7D2CB]">
                        {filteredAllTickets.map((ticket) => (
                          <tr key={ticket.id} className="hover:bg-[#D9D9D6]">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#101820]">{ticket.buyerName}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#101820]">{ticket.buyerEmail}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#FFA300] font-semibold">{ticket.hotelName}</td>
                            <td className="px-4 py-3"> {/* QR Code Cell */}
                              {qriousLoaded && ticket.qrCodeData ? (
                                <QRCodeCanvas value={ticket.qrCodeData} size={64} id={`admin-qr-${ticket.id}`} />
                              ) : (
                                <span className="text-xs text-[#AFA9A0]">Loading QR...</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {ticket.scanned ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-4 h-4 mr-1" /> Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle className="w-4 h-4 mr-1" /> No
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#AFA9A0]">
                              {new Date(ticket.purchaseDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#AFA9A0]">
                              {ticket.recordedByEmail || ticket.recordedBy}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleOpenQrDownloadModal(ticket.qrCodeData)}
                                className="text-[#AFA9A0] hover:text-[#101820] ml-2 focus:outline-none focus:ring-2 focus:ring-[#FFA300] focus:ring-opacity-50 rounded-full p-1 transition duration-150 ease-in-out"
                                title="Download QR Code"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleOpenDeleteModal(ticket)}
                                className="text-red-600 hover:text-red-900 ml-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 rounded-full p-1 transition duration-150 ease-in-out"
                                title="Delete Ticket"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          null
        )}

        {/* Add Ticket Modal */}
        {showAddTicketModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                onClick={() => setShowAddTicketModal(false)}
                className="absolute top-4 right-4 text-[#AFA9A0] hover:text-[#101820] transition duration-200"
              >
                <XCircle className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-[#101820] mb-6 text-center">Add New Ticket Sale</h2>
              <form onSubmit={handleAddTicket} className="space-y-5">
                <div>
                  <label htmlFor="buyerName" className="block text-[#101820] text-sm font-semibold mb-2">
                    <User className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Employee Name
                  </label>
                  <input
                    type="text"
                    id="buyerName"
                    ref={buyerNameRef}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter staff member's name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="buyerEmail" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Mail className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Employee Email
                  </label>
                  <input
                    type="email"
                    id="buyerEmail"
                    ref={buyerEmailRef}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter staff member's email"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="buyerMobile" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Smartphone className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="buyerMobile"
                    ref={buyerMobileRef}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter staff member's mobile number (Optional)"
                  />
                </div>
                <div>
                  <label htmlFor="buyerCompanyId" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Briefcase className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Company ID/ID Card
                  </label>
                  <input
                    type="text"
                    id="buyerCompanyId"
                    ref={buyerCompanyIdRef}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter staff member's Company ID/ID Card (Optional)"
                  />
                </div>
                <div>
                  <label htmlFor="hotelSelect" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Hotel className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Select Outlet
                  </label>
                  <select
                    id="hotelSelect"
                    ref={hotelSelectRef}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200 bg-white"
                    required
                  >
                    <option value="">-- Select Outlet --</option>
                    {HOTELS.map((outlet) => (
                      <option key={outlet} value={outlet}>{outlet}</option>
                    ))}
                  </select>
                </div>
                {addTicketStatus && addTicketMessage && (
                  <div
                    className={`p-3 rounded-lg flex items-center ${addTicketStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {addTicketStatus === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> : <XCircle className="w-5 h-5 mr-2" />}
                    <p className="font-semibold">{addTicketMessage}</p>
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full bg-[#FFA300] hover:bg-[#e69400] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-xl flex items-center justify-center transition duration-300 transform hover:scale-105"
                >
                  <Plus className="w-6 h-6 mr-2" /> Record Ticket Sale
                </button>
              </form>
            </div>
          </div>
        )}

        {/* QR Display Modal */}
        {showQrDisplayModal && qrToDownload && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                onClick={() => setShowQrDisplayModal(false)}
                className="absolute top-4 right-4 text-[#AFA9A0] hover:text-[#101820] transition duration-200"
              >
                <XCircle className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-[#101820] mb-6 text-center">QR Code for Ticket</h2>
              <div className="text-center space-y-6">
                {addTicketMessage && (
                  <p className="text-lg font-semibold text-green-700">{addTicketMessage}</p>
                )}
                <p className="text-[#101820]">Please save this QR code image and send it to the staff member:</p>
                {qriousLoaded ? (
                  <>
                    <QRCodeCanvas value={qrToDownload} size={256} id="qr-display-modal-canvas" />
                    <p className="text-sm font-mono text-[#AFA9A0] break-words mt-2">
                      QR Data: <span className="font-bold text-[#101820]">{qrToDownload}</span>
                    </p>
                    <button
                      onClick={handleDownloadQrCode}
                      className="bg-[#AFA9A0] hover:bg-[#969088] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center mx-auto transition duration-300 transform hover:scale-105"
                    >
                      <Download className="w-6 h-6 mr-2" /> Download QR Code
                    </button>
                    <button
                      onClick={() => setShowQrDisplayModal(false)}
                      className="mt-4 bg-[#D7D2CB] hover:bg-[#c4bfb8] text-[#101820] font-bold py-2 px-4 rounded-lg shadow transition duration-300"
                    >
                      Close
                    </button>
                  </>
                ) : (
                  <p className="text-orange-500">Loading QR code generator...</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Last Entry Modal */}
        {showLastEntryModal && lastEntryTicketDetails && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                onClick={() => setShowLastEntryModal(false)}
                className="absolute top-4 right-4 text-[#AFA9A0] hover:text-[#101820] transition duration-200"
              >
                <XCircle className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-[#101820] mb-6 text-center">Review Last Entry</h2>
              <form onSubmit={handleUpdateLastEntry} className="space-y-5">
                <div>
                  <label htmlFor="editBuyerName" className="block text-[#101820] text-sm font-semibold mb-2">
                    <User className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Employee Name
                  </label>
                  <input
                    type="text"
                    id="editBuyerName"
                    value={editingBuyerName}
                    onChange={(e) => setEditingBuyerName(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="editBuyerEmail" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Mail className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Employee Email
                  </label>
                  <input
                    type="email"
                    id="editBuyerEmail"
                    value={editingBuyerEmail}
                    onChange={(e) => setEditingBuyerEmail(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="editBuyerMobile" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Smartphone className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="editBuyerMobile"
                    value={editingBuyerMobile}
                    onChange={(e) => setEditingBuyerMobile(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="editBuyerCompanyId" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Briefcase className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Company ID/ID Card
                  </label>
                  <input
                    type="text"
                    id="editBuyerCompanyId"
                    value={editingBuyerCompanyId}
                    onChange={(e) => setEditingBuyerCompanyId(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="editHotelSelect" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Hotel className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Select Outlet
                  </label>
                  <select
                    id="editHotelSelect"
                    value={editingHotelName}
                    onChange={(e) => setEditingHotelName(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200 bg-white"
                    required
                  >
                    <option value="">-- Select Outlet --</option>
                    {HOTELS.map((outlet) => (
                      <option key={`edit-${outlet}`} value={outlet}>{outlet}</option>
                    ))}
                  </select>
                </div>
                {lastEntryEditStatus && lastEntryEditMessage && (
                  <div
                    className={`p-3 rounded-lg flex items-center ${lastEntryEditStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {lastEntryEditStatus === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> : <XCircle className="w-5 h-5 mr-2" />}
                    <p className="font-semibold">{lastEntryEditMessage}</p>
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full bg-[#FFA300] hover:bg-[#e69400] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-xl flex items-center justify-center transition duration-300 transform hover:scale-105"
                >
                  Save Changes
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {showDeleteConfirmModal && ticketToDelete && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                onClick={() => setShowDeleteConfirmModal(false)}
                className="absolute top-4 right-4 text-[#AFA9A0] hover:text-[#101820] transition duration-200"
              >
                <XCircle className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-red-700 mb-4 text-center">Confirm Deletion</h2>
              <p className="text-[#101820] text-center mb-6">
                Are you sure you want to delete the ticket for <strong>{ticketToDelete.buyerName}</strong> from <strong>{ticketToDelete.hotelName}</strong>?
                This action cannot be undone.
              </p>
              <form onSubmit={handleConfirmDelete} className="space-y-4">
                <div>
                  <label htmlFor="deletePassword" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Lock className="inline-block w-4 h-4 mr-2 text-[#AFA9A0]" /> Enter Password to Confirm
                  </label>
                  <input
                    type="password"
                    id="deletePassword"
                    value={deletePasswordInput}
                    onChange={(e) => setDeletePasswordInput(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent transition duration-200"
                    placeholder="Enter password"
                    required
                  />
                </div>
                {deleteError && (
                  <p className="text-red-600 text-sm text-center">{deleteError}</p>
                )}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirmModal(false)}
                    className="bg-[#D7D2CB] hover:bg-[#c4bfb8] text-[#101820] font-bold py-2 px-4 rounded-lg shadow transition duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg shadow-md flex items-center transition duration-300 transform hover:scale-105"
                  >
                    <Trash2 className="w-5 h-5 mr-2" /> Delete Permanently
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Login/Signup Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                onClick={() => setShowAuthModal(false)}
                className="absolute top-4 right-4 text-[#AFA9A0] hover:text-[#101820] transition duration-200"
              >
                <XCircle className="w-7 h-7" />
              </button>
              <h2 className="text-2xl font-bold text-[#101820] mb-6 text-center">
                {isLoginMode ? 'Log In' : 'Sign Up'}
              </h2>
              <form onSubmit={handleAuthSubmit} className="space-y-5">
                <div>
                  <label htmlFor="authEmail" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Mail className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Email
                  </label>
                  <input
                    type="email"
                    id="authEmail"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="authPassword" className="block text-[#101820] text-sm font-semibold mb-2">
                    <Lock className="inline-block w-4 h-4 mr-2 text-[#FFA300]" /> Password
                  </label>
                  <input
                    type="password"
                    id="authPassword"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    className="w-full p-3 border border-[#AFA9A0] rounded-lg shadow-sm focus:ring-2 focus:ring-[#FFA300] focus:border-transparent transition duration-200"
                    placeholder="Enter your password"
                    required
                  />
                </div>
                {authError && (
                  <p className="text-red-600 text-sm text-center">{authError}</p>
                )}
                <button
                  type="submit"
                  className="w-full bg-[#FFA300] hover:bg-[#e69400] text-[#101820] font-bold py-3 px-6 rounded-lg shadow-xl flex items-center justify-center transition duration-300 transform hover:scale-105"
                >
                  {isLoginMode ? 'Log In' : 'Sign Up'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          body {
            font-family: 'Inter', sans-serif;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
}

export default App;
